import {MetricSeriesAnalysis, MetricTrendDirection} from "@/lib/types/metricsExplorer.ts";
import {formatMetricValue} from "@/pages/metricsExplorer/chartSeries.ts";

export type FigureTone = "up" | "down";

export interface Figure {
  label: string;
  value: string;
  /**
   * The direction the number moved, not a class name: rising response time is bad and
   * falling is good, and a bare figure does not say so. How that is painted stays with the
   * component, so this file needs no stylesheet.
   */
  tone?: FigureTone;
}

const HOUR_LABELS = Array.from({length: 24}, (_, hour) => `${String(hour).padStart(2, "0")}:00`);

/** A flat trend is neither good nor bad, so it is left untoned. */
const TREND_TONE: Partial<Record<MetricTrendDirection, FigureTone>> = {UP: "up", DOWN: "down"};

/**
 * A p-value small enough to round to zero still is not zero, and printing "0.000" would
 * claim a certainty the test cannot give.
 */
export const formatPValue = (value: number): string => (value < 0.001 ? "<0.001" : value.toFixed(3));

/** The scalars one series' analysis produced, in the order they are read. */
export const buildFigures = (entry: MetricSeriesAnalysis, unit: string | null): Figure[] => {
  const figures: Figure[] = [];
  if (entry.trend) {
    figures.push({
      label: "Trend",
      value: `${formatMetricValue(entry.trend.slope_per_day, unit)} / day`,
      tone: TREND_TONE[entry.trend.direction],
    });
    if (entry.trend.slope_ci_low !== null && entry.trend.slope_ci_high !== null) {
      figures.push({
        label: `${Math.round(entry.trend.confidence_level * 100)}% CI`,
        value: `${formatMetricValue(entry.trend.slope_ci_low, unit)} to ${formatMetricValue(
          entry.trend.slope_ci_high,
          unit
        )} / day`,
      });
    }
    if (entry.trend.p_value !== null) {
      figures.push({label: "p-value", value: formatPValue(entry.trend.p_value)});
    }
    figures.push({label: "Fit (R²)", value: entry.trend.r_squared.toFixed(2)});
    if (entry.trend.pct_change_over_window !== null) {
      figures.push({
        label: "Change over window",
        value: `${entry.trend.pct_change_over_window > 0 ? "+" : ""}${entry.trend.pct_change_over_window.toFixed(1)}%`,
      });
    }
  }
  if (entry.baseline) {
    figures.push({label: "Mean", value: formatMetricValue(entry.baseline.mean, unit)});
    figures.push({label: "Median", value: formatMetricValue(entry.baseline.p50, unit)});
    figures.push({label: "IQR", value: formatMetricValue(entry.baseline.iqr, unit)});
    figures.push({label: "95th pct", value: formatMetricValue(entry.baseline.p95, unit)});
  }
  if (entry.anomaly) {
    figures.push({
      label: "Outliers",
      value: `${entry.anomaly.outlier_count} (${(entry.anomaly.outlier_ratio * 100).toFixed(1)}%)`,
      tone: entry.anomaly.outlier_count > 0 ? "up" : undefined,
    });
    if (entry.anomaly.current_zscore !== null) {
      figures.push({label: "Latest (σ)", value: entry.anomaly.current_zscore.toFixed(2)});
    }
  }
  if (entry.seasonality) {
    figures.push({label: "Seasonality", value: `${(entry.seasonality.strength * 100).toFixed(0)}%`});
    if (entry.seasonality.peak_hour !== null) {
      figures.push({label: "Busiest hour", value: HOUR_LABELS[entry.seasonality.peak_hour]});
    }
  }
  figures.push({
    label: "Samples",
    value: `${entry.window.datapoint_count}${entry.window.gap_count > 0 ? ` (${entry.window.gap_count} gaps)` : ""}`,
  });
  return figures;
};
